class ZohClientReviews extends HTMLElement {
  #viewport = null;
  #track = null;
  #prev = null;
  #next = null;
  #progressFill = null;
  #slides = [];
  #originalSlides = [];
  #gap = 10;
  #peek = 10;
  #index = 0;
  #position = 0;
  #cloneCount = 0;
  #resizeObserver = null;
  #rafId = null;
  #dragRafId = null;
  #isJumping = false;
  #isDragging = false;
  #pointerId = null;
  #startX = 0;
  #startY = 0;
  #deltaX = 0;
  #pendingDeltaX = 0;
  #moved = false;
  #axisLocked = null;
  #dragOrigin = 0;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-zoh-cr-viewport]');
    this.#track = this.querySelector('[data-zoh-cr-track]');
    this.#prev = this.querySelector('[data-zoh-cr-prev]');
    this.#next = this.querySelector('[data-zoh-cr-next]');
    this.#progressFill = this.querySelector('[data-zoh-cr-progress-fill]');
    this.#originalSlides = Array.from(this.querySelectorAll('[data-zoh-cr-slide]'));

    if (!this.#viewport || !this.#track || this.#originalSlides.length === 0) return;

    this.#setupLoop();

    this.#prev?.addEventListener('click', this.#onPrev);
    this.#next?.addEventListener('click', this.#onNext);
    this.#track.addEventListener('transitionend', this.#onTransitionEnd);
    this.#viewport.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    this.#track.addEventListener('click', this.#onTrackClick, true);
    this.#track.addEventListener('keydown', this.#onKeyDown);
    window.addEventListener('resize', this.#onResize, { passive: true });
    window.addEventListener('load', this.#onResize, { passive: true });

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.#isDragging) this.#render(false);
      });
      this.#resizeObserver.observe(this.#viewport);
    }

    this.#index = this.#getInitialIndex();
    this.#position = this.#cloneCount + this.#index;
    this.#render(false);
  }

  disconnectedCallback() {
    this.#prev?.removeEventListener('click', this.#onPrev);
    this.#next?.removeEventListener('click', this.#onNext);
    this.#track?.removeEventListener('transitionend', this.#onTransitionEnd);
    this.#viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#unbindWindowPointer();
    this.#track?.removeEventListener('click', this.#onTrackClick, true);
    this.#track?.removeEventListener('keydown', this.#onKeyDown);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#onResize);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
    if (this.#dragRafId !== null) cancelAnimationFrame(this.#dragRafId);
  }

  #bindWindowPointer() {
    window.addEventListener('pointermove', this.#onPointerMove, { passive: false });
    window.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    window.addEventListener('pointercancel', this.#onPointerUp, { passive: true });
  }

  #unbindWindowPointer() {
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
  }

  #isMobile() {
    return window.innerWidth < 750;
  }

  #getInitialIndex() {
    if (window.innerWidth >= 1024) return Math.min(2, this.#originalSlides.length - 1);
    if (window.innerWidth >= 750) return Math.min(1, this.#originalSlides.length - 1);
    return 0;
  }

  #getGap() {
    const root = this.closest('.zoh-client-reviews') || this;
    const value = Number.parseFloat(window.getComputedStyle(root).getPropertyValue('--zoh-cr-gap'));
    if (!Number.isNaN(value)) this.#gap = value;
    return this.#gap;
  }

  #getPeek() {
    const root = this.closest('.zoh-client-reviews') || this;
    const value = Number.parseFloat(window.getComputedStyle(root).getPropertyValue('--zoh-cr-peek'));
    if (!Number.isNaN(value)) this.#peek = value;
    return this.#isMobile() ? this.#peek || 10 : 0;
  }

  #getSlideWidth(viewportWidth, gap, peek) {
    if (this.#isMobile()) {
      return Math.max(200, viewportWidth - peek * 2 - gap * 2);
    }

    const firstSlide = this.#slides[0];
    if (firstSlide instanceof HTMLElement) {
      const width = firstSlide.getBoundingClientRect().width;
      if (width > 0) return width;
    }

    return 281;
  }

  #getStep() {
    const viewportWidth = this.#viewport?.clientWidth || 0;
    const gap = this.#getGap();
    const peek = this.#getPeek();
    const slideWidth = this.#getSlideWidth(viewportWidth, gap, peek);
    return { viewportWidth, gap, peek, slideWidth, step: slideWidth + gap };
  }

  #translateFor(position) {
    const { viewportWidth, gap, peek, slideWidth, step } = this.#getStep();
    this.#syncCardWidth(slideWidth);
    return {
      translate: viewportWidth / 2 - slideWidth / 2 - position * step,
      step,
      slideWidth,
      gap,
      peek,
      viewportWidth,
    };
  }

  #syncCardWidth(slideWidth) {
    const root = this.closest('.zoh-client-reviews');
    if (!(root instanceof HTMLElement)) return;

    if (this.#isMobile()) {
      root.style.setProperty('--zoh-cr-card-width', `${slideWidth}px`);
    } else {
      root.style.removeProperty('--zoh-cr-card-width');
    }
  }

  #readCurrentTranslate() {
    if (!(this.#track instanceof HTMLElement)) return 0;
    const style = window.getComputedStyle(this.#track);
    const matrix = style.transform;
    if (!matrix || matrix === 'none') return 0;

    if (matrix.startsWith('matrix3d(')) {
      const parts = matrix.slice(9, -1).split(',');
      return Number.parseFloat(parts[12]) || 0;
    }

    if (matrix.startsWith('matrix(')) {
      const parts = matrix.slice(7, -1).split(',');
      return Number.parseFloat(parts[4]) || 0;
    }

    return 0;
  }

  #setupLoop() {
    if (!(this.#track instanceof HTMLElement)) return;

    this.#track.querySelectorAll("[data-clone='true']").forEach((slide) => slide.remove());
    this.#slides = [...this.#originalSlides];
    this.#cloneCount = this.#originalSlides.length > 1 ? Math.min(3, this.#originalSlides.length) : 0;
    if (this.#cloneCount === 0) return;

    const prependClones = this.#originalSlides.slice(-this.#cloneCount).map((slide) => this.#createClone(slide));
    const appendClones = this.#originalSlides.slice(0, this.#cloneCount).map((slide) => this.#createClone(slide));
    prependClones.forEach((clone) => this.#track.insertBefore(clone, this.#track.firstChild));
    appendClones.forEach((clone) => this.#track.appendChild(clone));
    this.#slides = Array.from(this.#track.querySelectorAll('[data-zoh-cr-slide]'));
  }

  #createClone(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('data-shopify-editor-block');
    return clone;
  }

  #getRealIndex(position = this.#position) {
    const total = this.#originalSlides.length;
    if (total === 0) return 0;
    let realIndex = (position - this.#cloneCount) % total;
    if (realIndex < 0) realIndex += total;
    return realIndex;
  }

  #activePosition() {
    return this.#cloneCount > 0 ? this.#position : this.#index;
  }

  #setDragging(isDragging) {
    this.#isDragging = isDragging;
    this.#viewport?.classList.toggle('is-dragging', isDragging);
    if (this.#track instanceof HTMLElement) {
      this.#track.style.transition = isDragging ? 'none' : '';
    }
  }

  #setTranslate(value) {
    if (!(this.#track instanceof HTMLElement)) return;
    this.#track.style.transform = `translate3d(${value}px, 0, 0)`;
  }

  #updateEffects(activePosition) {
    const isMobile = this.#isMobile();

    this.#slides.forEach((slide, slideIndex) => {
      if (isMobile) {
        slide.style.transform = '';
        slide.style.zIndex = '';
        return;
      }

      const delta = slideIndex - activePosition;
      let rotate = 0;
      let translateY = 0;

      if (delta < 0) {
        rotate = Math.abs(delta) >= 2 ? -4 : -2;
        translateY = Math.abs(delta) >= 2 ? 8 : 4;
      } else if (delta > 0) {
        rotate = Math.abs(delta) >= 2 ? 4 : 2;
        translateY = Math.abs(delta) >= 2 ? 8 : 4;
      }

      slide.style.transform = `translate3d(0, ${translateY}%, 0) rotate(${rotate}deg)`;
      slide.style.zIndex = String(100 - Math.abs(delta));
    });
  }

  #updateProgress() {
    if (!(this.#progressFill instanceof HTMLElement) || !this.#originalSlides.length) return;
    this.#progressFill.style.width = `${((this.#index + 1) / this.#originalSlides.length) * 100}%`;
  }

  #updateSlideInert(activePosition) {
    this.#slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === activePosition;
      const isClone = slide.hasAttribute('data-clone');

      if (isClone || !isActive) {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', '');
      } else {
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('inert');
      }
    });
  }

  #render(animate = true) {
    if (!this.#track || this.#isDragging) return;

    const activePosition = this.#activePosition();
    const { translate } = this.#translateFor(activePosition);

    if (!animate) {
      this.#track.style.transition = 'none';
      this.#setTranslate(translate);
      this.#updateEffects(activePosition);
      this.#updateSlideInert(activePosition);
      this.#updateProgress();
      void this.#track.offsetWidth;
      this.#track.style.transition = '';
      return;
    }

    this.#track.style.transition = '';
    this.#setTranslate(translate);
    this.#updateEffects(activePosition);
    this.#updateSlideInert(activePosition);
    this.#updateProgress();
  }

  #stepBy(dir) {
    if (this.#originalSlides.length < 2 || this.#isDragging) return;

    const direction = dir < 0 ? -1 : 1;

    if (this.#cloneCount === 0) {
      const total = this.#originalSlides.length;
      this.#index = (this.#index + direction + total) % total;
      this.#position = this.#index;
    } else {
      this.#position += direction;
      this.#index = this.#getRealIndex();
    }

    this.#render(true);
  }

  #onPrev = () => this.#stepBy(-1);
  #onNext = () => this.#stepBy(1);

  #onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#stepBy(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#stepBy(1);
    }
  };

  #onPointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    if (!this.#viewport || !this.#track || this.#originalSlides.length < 2) return;

    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#startY = event.clientY;
    this.#deltaX = 0;
    this.#pendingDeltaX = 0;
    this.#moved = false;
    this.#axisLocked = null;

    this.#dragOrigin = this.#readCurrentTranslate();
    this.#setDragging(true);
    this.#setTranslate(this.#dragOrigin);
    this.#bindWindowPointer();
    this.#viewport.setPointerCapture?.(event.pointerId);
  };

  #onPointerMove = (event) => {
    if (!this.#isDragging || event.pointerId !== this.#pointerId) return;

    const dx = event.clientX - this.#startX;
    const dy = event.clientY - this.#startY;

    if (this.#axisLocked === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;

      if (Math.abs(dy) > Math.abs(dx)) {
        this.#finishDrag(false);
        return;
      }

      this.#axisLocked = 'x';
    }

    if (this.#axisLocked !== 'x') return;

    event.preventDefault();
    this.#pendingDeltaX = dx;
    this.#moved = Math.abs(dx) > 8;

    if (this.#dragRafId !== null) return;

    this.#dragRafId = requestAnimationFrame(() => {
      this.#dragRafId = null;
      if (!this.#isDragging) return;

      const { step } = this.#getStep();
      let dragX = this.#pendingDeltaX;

      if (Math.abs(dragX) > step) {
        const extra = Math.abs(dragX) - step;
        dragX = Math.sign(dragX) * (step + extra * 0.2);
      }

      this.#deltaX = dragX;
      this.#setTranslate(this.#dragOrigin + dragX);
    });
  };

  #onPointerUp = (event) => {
    if (!this.#isDragging || event.pointerId !== this.#pointerId) return;
    this.#finishDrag(true);
  };

  #finishDrag(shouldSnap) {
    if (!this.#isDragging) return;

    const deltaX = this.#deltaX;
    const { step } = this.#translateFor(this.#activePosition());
    const threshold = Math.max(24, step * 0.15);

    if (this.#viewport?.hasPointerCapture?.(this.#pointerId)) {
      this.#viewport.releasePointerCapture(this.#pointerId);
    }

    this.#unbindWindowPointer();
    this.#pointerId = null;
    this.#setDragging(false);

    if (this.#dragRafId !== null) {
      cancelAnimationFrame(this.#dragRafId);
      this.#dragRafId = null;
    }

    if (shouldSnap && Math.abs(deltaX) >= threshold) {
      this.#stepBy(deltaX < 0 ? 1 : -1);
    } else {
      this.#render(true);
    }

    this.#deltaX = 0;
    this.#pendingDeltaX = 0;
    this.#moved = Math.abs(deltaX) > 8;
    this.#axisLocked = null;
  }

  #onTrackClick = (event) => {
    if (!this.#moved) return;
    event.preventDefault();
    event.stopPropagation();
    this.#moved = false;
  };

  #onResize = () => {
    this.#index = Math.min(this.#index, Math.max(this.#originalSlides.length - 1, 0));
    this.#position = this.#cloneCount > 0 ? this.#cloneCount + this.#index : this.#index;
    this.#render(false);
  };

  #onTransitionEnd = (event) => {
    if (event.target !== this.#track || event.propertyName !== 'transform') return;
    if (this.#isJumping || this.#cloneCount === 0 || this.#isDragging) return;

    const total = this.#originalSlides.length;
    const minPosition = this.#cloneCount;
    const maxPosition = this.#cloneCount + total - 1;
    let jumpTo = null;

    if (this.#position < minPosition) jumpTo = this.#position + total;
    else if (this.#position > maxPosition) jumpTo = this.#position - total;

    if (jumpTo === null) return;

    this.#isJumping = true;
    this.#position = jumpTo;
    this.#index = this.#getRealIndex();
    this.#render(false);

    requestAnimationFrame(() => {
      this.#isJumping = false;
    });
  };
}

if (!customElements.get('zoh-client-reviews')) {
  customElements.define('zoh-client-reviews', ZohClientReviews);
}
